//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import { SamplingCatalogProductsResponse } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import {
  searchBvCatalogProducts,
  searchCuralateCatalogProducts,
} from '@apis/universal-image-actions/universalImageActionsApi';
import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import PartialCuid from '@models/PartialCuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined } from '@utils/filterPredicates';

const mapBVResponseToCuralateProducts = ({
  products,
}: SamplingCatalogProductsResponse): CuralateCatalogProduct[] => {
  if (isDefined(products)) {
    return products.reduce<CuralateCatalogProduct[]>(
      (formattedProducts, product) => {
        if (isDefined(product.styleId) && !!product.productDetailPageUrl) {
          formattedProducts.push({
            destinationUrl: product.productDetailPageUrl,
            images: [
              {
                fileId: product.fileId,
                imageUrl: product.imageUrl,
                sourceImageUrl: product.imageUrl,
              },
            ],
            name: product.name,
            productStyleId: product.styleId,
            styleId: product.styleId,
            description: product.description,
            externalId: product.samplingProductId,
          });
        }

        return formattedProducts;
      },
      [],
    );
  }
  return [];
};

export interface ProductsParams {
  hasCatalog: boolean;
  isBvCampaignTagged?: boolean;
  isEnabled: boolean;
  keywords: string;
  partialCuid: PartialCuid;
  limit: number;
  offset: number;
}

export const useProductsQuery = ({
  hasCatalog,
  isBvCampaignTagged = false,
  isEnabled,
  keywords,
  limit,
  offset,
  partialCuid,
}: ProductsParams) =>
  useQuery({
    enabled: isEnabled && hasCatalog, // TODO: PD-282907 - Remove ieEnabled when the UIA Products Api Port feature flag is removed
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsProducts,
      QueryKeyConstants.UniversalImageActionsProductsList,
      { isBvCampaignTagged, keywords, partialCuid, limit, offset },
    ],
    queryFn: async ({ signal }) => {
      if (isBvCampaignTagged) {
        return mapBVResponseToCuralateProducts(
          await searchBvCatalogProducts({ keywords }, { signal }),
        );
      } else {
        const res = await searchCuralateCatalogProducts(
          { keywords, partialCuid, limit, offset },
          { signal },
        );
        return res.products;
      }
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
